/**
 * Upload Library - PDF Library Management
 * Multi-step upload wizard with CLIENT-SIDE page splitting and folder hierarchy
 *
 * PDF splitting happens in the browser using pdf-lib to:
 * - Reduce server load
 * - Minimize bandwidth (only upload selected pages)
 * - Faster upload experience
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { PDFDocument } from "pdf-lib";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Drawer from "@mui/material/Drawer";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Context
import { useMaterialUIController } from "context";

// API Configuration
import API_BASE_URL from "config/api";
import { isLibraryRootFolder, normalizeFolderPathNames } from "constants/libraryRoots";

// dynamic limit will be fetched from backend

/**
 * Read file as ArrayBuffer
 */
const readFileAsArrayBuffer = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Get page count from PDF file (client-side)
 */
const getPageCount = async (file) => {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const pdfDoc = await PDFDocument.load(arrayBuffer);
  return pdfDoc.getPageCount();
};

/**
 * Split PDF to extract specific page range (client-side)
 * Returns a new File object with only the selected pages
 */
const splitPdf = async (file, fromPage, toPage) => {
  const arrayBuffer = await readFileAsArrayBuffer(file);
  const sourcePdf = await PDFDocument.load(arrayBuffer);

  // Create new PDF with selected pages
  const newPdf = await PDFDocument.create();

  // pdf-lib uses 0-indexed pages
  const pageIndices = [];
  for (let i = fromPage - 1; i < toPage; i++) {
    pageIndices.push(i);
  }

  const copiedPages = await newPdf.copyPages(sourcePdf, pageIndices);
  copiedPages.forEach((page) => newPdf.addPage(page));

  // Save as bytes
  const pdfBytes = await newPdf.save();

  // Convert to File object
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const splitFileName = file.name.replace(".pdf", `_pages_${fromPage}-${toPage}.pdf`);
  return new File([blob], splitFileName, { type: "application/pdf" });
};

const Variation1Folder = ({ folder, count, onClick, onContextMenu, darkMode }) => {
  return (
    <div
      className="relative group flex flex-col items-center justify-center w-full mt-4"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div className="file relative w-28 h-20 sm:w-40 sm:h-28 cursor-pointer origin-bottom [perspective:1500px] z-10 transition-transform hover:scale-105">
        <div className="work-5 bg-amber-600 w-full h-full origin-top rounded-xl rounded-tl-none group-hover:shadow-[0_20px_40px_rgba(0,0,0,.3)] transition-all ease duration-300 relative after:absolute after:content-[''] after:bottom-[99%] after:left-0 after:w-10 sm:after:w-12 after:h-3 after:bg-amber-600 after:rounded-t-xl before:absolute before:content-[''] before:-top-[11px] before:left-[35px] sm:before:left-[45.5px] before:w-3 before:h-3 before:bg-amber-600 before:[clip-path:polygon(0_35%,0%_100%,50%_100%);]" />
        <div className="work-4 absolute inset-1 bg-zinc-600 rounded-xl transition-all ease duration-300 origin-bottom select-none group-hover:[transform:rotateX(-20deg)]" />
        <div className="work-3 absolute inset-1 bg-zinc-500 rounded-xl transition-all ease duration-300 origin-bottom group-hover:[transform:rotateX(-30deg)]" />
        <div className="work-2 absolute inset-1 bg-zinc-400 rounded-xl transition-all ease duration-300 origin-bottom group-hover:[transform:rotateX(-38deg)]" />
        <div className="work-1 absolute bottom-0 bg-gradient-to-t from-amber-500 to-amber-400 w-full h-[75px] sm:h-[110px] rounded-xl rounded-tr-none after:absolute after:content-[''] after:bottom-[99%] after:right-0 after:w-[70px] sm:after:w-[100px] after:h-[10px] sm:after:h-[12px] after:bg-amber-400 after:rounded-t-xl before:absolute before:content-[''] before:-top-[6px] sm:before:-top-[8px] before:right-[68px] sm:before:right-[97px] before:size-2 before:bg-amber-400 before:[clip-path:polygon(100%_14%,50%_100%,100%_100%);] transition-all ease duration-300 origin-bottom flex items-end group-hover:shadow-[inset_0_20px_40px_#fbbf24,_inset_0_-20px_40px_#d97706] group-hover:[transform:rotateX(-46deg)_translateY(1px)]" />
      </div>
      <div className="mt-4 text-center px-2">
        <h4
          className={`text-[12px] sm:text-[14px] font-bold truncate w-28 sm:w-40 ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
          title={folder.name}
        >
          {folder.name}
        </h4>
        <span
          className={`text-[11px] sm:text-[12px] font-medium ${
            darkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          {count} docs
        </span>
      </div>
    </div>
  );
};

Variation1Folder.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const Variation2Folder = ({ folder, count, onClick, onContextMenu, darkMode }) => {
  return (
    <div
      className="relative group flex flex-col items-center justify-center w-full mt-4 animate-fadeIn"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div className="relative w-28 h-20 sm:w-44 sm:h-28 cursor-pointer">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${
            darkMode ? "from-violet-500 to-violet-600" : "from-violet-400 to-violet-500"
          } rounded-lg transition-all duration-300 group-hover:shadow-2xl group-hover:scale-105`}
        >
          <div
            className={`absolute -top-2 left-0 w-10 sm:w-16 h-4 ${
              darkMode ? "bg-violet-600" : "bg-violet-500"
            } rounded-t-lg`}
          />
          <div className="absolute inset-2 bg-violet-400/20 rounded-md backdrop-blur-sm" />
          <div className="absolute bottom-2 left-2 right-2 space-y-1">
            <div className="h-1 bg-white/30 rounded w-3/4" />
            <div className="h-1 bg-white/30 rounded w-1/2" />
          </div>
        </div>
      </div>
      <div className="mt-4 text-center px-2">
        <h4
          className={`text-[12px] sm:text-[14px] font-bold truncate w-28 sm:w-40 ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
          title={folder.name}
        >
          {folder.name}
        </h4>
        <span
          className={`text-[11px] sm:text-[12px] font-medium ${
            darkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          {count} docs
        </span>
      </div>
    </div>
  );
};

Variation2Folder.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const Variation3Folder = ({ folder, count, onClick, onContextMenu, darkMode }) => {
  return (
    <div
      className="relative group flex flex-col items-center justify-center w-full mt-4 animate-fadeIn"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div className="relative w-28 h-20 sm:w-44 sm:h-28 cursor-pointer">
        <div
          className={`absolute inset-0 bg-gradient-to-br ${
            darkMode
              ? "from-purple-900 to-pink-900 border-purple-500"
              : "from-purple-100 to-pink-100 border-purple-300"
          } rounded-xl border-2 transition-all duration-500 group-hover:border-pink-500 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.6)]`}
        >
          <div
            className={`absolute -top-3 left-2 w-12 sm:w-20 h-5 bg-gradient-to-r ${
              darkMode
                ? "from-purple-600 to-pink-600 border-purple-500"
                : "from-purple-400 to-pink-400 border-purple-300"
            } rounded-t-xl border-2 border-b-0 group-hover:border-pink-500 transition-all duration-500`}
          />
          <div
            className={`absolute inset-1.5 border ${
              darkMode ? "border-purple-500/30" : "border-purple-300/30"
            } rounded-lg group-hover:border-pink-500/50 transition-all duration-500`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-purple-500/20 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>
      </div>
      <div className="mt-4 text-center px-2">
        <h4
          className={`text-[12px] sm:text-[14px] font-bold truncate w-28 sm:w-40 ${
            darkMode ? "text-purple-300" : "text-purple-700"
          }`}
          title={folder.name}
        >
          {folder.name}
        </h4>
        <span
          className={`text-[11px] sm:text-[12px] font-medium ${
            darkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          {count} docs
        </span>
      </div>
    </div>
  );
};

Variation3Folder.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const Variation4Folder = ({ folder, count, onClick, onContextMenu, darkMode }) => {
  return (
    <div
      className="relative group flex flex-col items-center justify-center w-full mt-4 animate-fadeIn"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div className="relative w-28 h-20 sm:w-40 sm:h-28 cursor-pointer">
        <div
          className={`absolute top-4 left-1 w-full h-full ${
            darkMode ? "bg-emerald-200/20" : "bg-emerald-600/10"
          } rounded-lg shadow-md transition-all duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-1.5`}
        />
        <div
          className={`absolute top-2 left-0.5 w-full h-full ${
            darkMode ? "bg-emerald-300/30" : "bg-emerald-600/20"
          } rounded-lg shadow-md transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1`}
        />
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-lg shadow-lg transition-all duration-300 group-hover:shadow-2xl">
          <div className="absolute -top-1.5 left-4 w-10 sm:w-16 h-4 bg-emerald-500 rounded-t-lg shadow-md" />
          <div className="absolute top-4 left-2 right-2 h-0.5 bg-emerald-600/30 rounded" />
          <div className="absolute top-6 left-2 right-4 h-0.5 bg-emerald-600/30 rounded" />
        </div>
      </div>
      <div className="mt-8 text-center px-2">
        <h4
          className={`text-[12px] sm:text-[14px] font-bold truncate w-28 sm:w-40 ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
          title={folder.name}
        >
          {folder.name}
        </h4>
        <span
          className={`text-[11px] sm:text-[12px] font-medium ${
            darkMode ? "text-gray-500" : "text-gray-600"
          }`}
        >
          {count} docs
        </span>
      </div>
    </div>
  );
};

Variation4Folder.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const Variation5Folder = ({ folder, count, onClick, onContextMenu, darkMode }) => {
  return (
    <div
      className="relative group flex flex-col items-center justify-center w-full mt-4 animate-fadeIn"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div className="relative w-28 h-20 sm:w-44 sm:h-28 cursor-pointer">
        <div
          className={`absolute inset-0 ${
            darkMode ? "bg-white/10 border-white/20" : "bg-black/5 border-black/15"
          } backdrop-blur-md rounded-2xl border shadow-xl transition-all duration-500 group-hover:bg-white/15 group-hover:shadow-2xl group-hover:scale-105`}
        >
          <div
            className={`absolute -top-2 left-4 w-12 sm:w-24 h-5 ${
              darkMode ? "bg-white/10 border-white/20" : "bg-black/5 border-black/15"
            } backdrop-blur-md rounded-t-2xl border border-b-0`}
          />
          <div className="absolute inset-3 rounded-xl bg-gradient-to-br from-white/10 to-transparent" />
          <div className="absolute bottom-2 left-3 right-3 space-y-1">
            <div className="h-1 bg-slate-400/40 rounded-full w-4/5" />
            <div className="h-1 bg-slate-400/40 rounded-full w-3/5" />
          </div>
        </div>
      </div>
      <div className="mt-4 text-center px-2">
        <h4
          className={`text-[12px] sm:text-[14px] font-bold truncate w-28 sm:w-40 ${
            darkMode ? "text-slate-300" : "text-slate-700"
          }`}
          title={folder.name}
        >
          {folder.name}
        </h4>
        <span
          className={`text-[11px] sm:text-[12px] font-medium ${
            darkMode ? "text-slate-500" : "text-slate-600"
          }`}
        >
          {count} docs
        </span>
      </div>
    </div>
  );
};

Variation5Folder.propTypes = {
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const FolderItem = ({ style, folder, count, onClick, onContextMenu, darkMode }) => {
  switch (style) {
    case "minimalFlat":
      return (
        <Variation2Folder
          folder={folder}
          count={count}
          onClick={onClick}
          onContextMenu={onContextMenu}
          darkMode={darkMode}
        />
      );
    case "neonGlow":
      return (
        <Variation3Folder
          folder={folder}
          count={count}
          onClick={onClick}
          onContextMenu={onContextMenu}
          darkMode={darkMode}
        />
      );
    case "stackedPaper":
      return (
        <Variation4Folder
          folder={folder}
          count={count}
          onClick={onClick}
          onContextMenu={onContextMenu}
          darkMode={darkMode}
        />
      );
    case "glassmorphism":
      return (
        <Variation5Folder
          folder={folder}
          count={count}
          onClick={onClick}
          onContextMenu={onContextMenu}
          darkMode={darkMode}
        />
      );
    case "amber3d":
    default:
      return (
        <Variation1Folder
          folder={folder}
          count={count}
          onClick={onClick}
          onContextMenu={onContextMenu}
          darkMode={darkMode}
        />
      );
  }
};

FolderItem.propTypes = {
  style: PropTypes.string.isRequired,
  folder: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

const DarkFileCard = ({ book, viewMode, onClick, onContextMenu, darkMode }) => {
  if (viewMode === "list") {
    return (
      <div
        className={`flex items-center px-4 py-3 ${
          darkMode
            ? "bg-bnova-surface border-bnova-border hover:bg-bnova-card/50"
            : "bg-white border-gray-200 hover:bg-gray-50"
        } border rounded-xl transition-colors cursor-pointer group mb-3 shadow-sm`}
        onClick={onClick}
        onContextMenu={onContextMenu}
      >
        <div
          className={`w-10 h-10 ${
            darkMode ? "bg-bnova-bg border-bnova-border" : "bg-gray-50 border-gray-200"
          } rounded-lg border flex items-center justify-center mr-4 relative`}
        >
          <div
            className={`w-4 h-0.5 ${
              darkMode ? "bg-[#475569]" : "bg-gray-400"
            } rounded-full absolute top-2 left-2`}
          ></div>
          <div
            className={`w-6 h-0.5 ${
              darkMode ? "bg-[#475569]" : "bg-gray-400"
            } rounded-full absolute top-4 left-2`}
          ></div>
          <div
            className={`w-5 h-0.5 ${
              darkMode ? "bg-[#475569]" : "bg-gray-400"
            } rounded-full absolute top-6 left-2`}
          ></div>
          <span className="absolute -bottom-1 -right-1 bg-bnova-danger text-white text-[8px] font-bold px-1 rounded-sm shadow-md">
            PDF
          </span>
        </div>
        <div className="flex-1 overflow-hidden">
          <h4
            className={`text-[14px] font-medium ${
              darkMode ? "text-bnova-textPrimary" : "text-gray-800"
            } truncate group-hover:text-bnova-primary transition-colors`}
          >
            {book.book_title}
          </h4>
        </div>
        <div
          className={`w-32 text-[12px] ${
            darkMode ? "text-bnova-textSecondary" : "text-gray-500"
          } hidden sm:block`}
        >
          {new Date(book.created_at || Date.now()).toLocaleDateString()}
        </div>
        <div
          className={`w-24 text-[12px] ${
            darkMode ? "text-bnova-textSecondary" : "text-gray-500"
          } hidden md:block`}
        >
          {book.chunk_count} chunks
        </div>
        <button
          className={`ml-2 p-1.5 rounded-lg ${
            darkMode
              ? "text-bnova-textSecondary hover:text-white hover:bg-white/10"
              : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          } transition-all`}
          onClick={(e) => {
            e.stopPropagation();
            onContextMenu(e);
          }}
          title="More options"
        >
          <Icon fontSize="small">more_vert</Icon>
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col cursor-pointer group relative"
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div
        className={`w-full aspect-[4/5] ${
          darkMode
            ? "bg-bnova-surface border-bnova-border group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.4)]"
            : "bg-white border-gray-200 group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
        } border rounded-xl relative flex flex-col p-4 overflow-hidden group-hover:border-bnova-primary/30 transition-all`}
      >
        {/* 3-dot menu button */}
        <button
          className={`absolute top-2 right-2 z-10 p-1 rounded-lg ${
            darkMode
              ? "text-bnova-textSecondary hover:text-white hover:bg-white/15"
              : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          } transition-all`}
          onClick={(e) => {
            e.stopPropagation();
            onContextMenu(e);
          }}
          title="More options"
        >
          <Icon fontSize="small">more_vert</Icon>
        </button>
        {/* Decorative lines inside to simulate document */}
        <div className="space-y-3 mt-4 opacity-50">
          <div
            className={`w-full h-1.5 ${darkMode ? "bg-[#334155]" : "bg-gray-200"} rounded-full`}
          ></div>
          <div
            className={`w-5/6 h-1.5 ${darkMode ? "bg-[#334155]" : "bg-gray-200"} rounded-full`}
          ></div>
          <div
            className={`w-4/6 h-1.5 ${darkMode ? "bg-[#334155]" : "bg-gray-200"} rounded-full`}
          ></div>
          <div
            className={`w-full h-1.5 ${
              darkMode ? "bg-[#334155]" : "bg-gray-200"
            } rounded-full mt-6`}
          ></div>
          <div
            className={`w-3/4 h-1.5 ${darkMode ? "bg-[#334155]" : "bg-gray-200"} rounded-full`}
          ></div>
          <div
            className={`w-2/4 h-1.5 ${darkMode ? "bg-[#334155]" : "bg-gray-200"} rounded-full`}
          ></div>
        </div>
        <span className="absolute bottom-3 right-3 bg-bnova-danger text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md shadow-lg shadow-red-500/20">
          PDF
        </span>
      </div>
      <div className="mt-3 px-1 text-center">
        <h4
          className={`text-[13px] font-medium ${
            darkMode
              ? "text-bnova-textPrimary group-hover:text-white"
              : "text-gray-800 group-hover:text-bnova-primary"
          } truncate transition-colors`}
          title={book.book_title}
        >
          {book.book_title}
        </h4>
        <div
          className={`text-[11px] ${
            darkMode ? "text-bnova-textSecondary" : "text-gray-500"
          } mt-0.5`}
        >
          {book.chunk_count} chunks
        </div>
      </div>
    </div>
  );
};

DarkFileCard.propTypes = {
  book: PropTypes.shape({
    book_title: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chunk_count: PropTypes.number,
  }).isRequired,
  viewMode: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func.isRequired,
  darkMode: PropTypes.bool.isRequired,
};

/** Normalize book folder paths from API (array, JSON string, or S3 key). */
const normalizeBookForLibrary = (book) => {
  if (!book || typeof book !== "object") return book;

  let folderPath = [];
  if (Array.isArray(book.folder_path)) {
    folderPath = normalizeFolderPathNames(book.folder_path);
  } else if (typeof book.folder_path === "string" && book.folder_path.trim()) {
    try {
      const parsed = JSON.parse(book.folder_path);
      folderPath = normalizeFolderPathNames(
        Array.isArray(parsed) ? parsed : book.folder_path.split("/")
      );
    } catch (_error) {
      folderPath = normalizeFolderPathNames(book.folder_path.split("/"));
    }
  }

  if (folderPath.length === 0 && book.s3_key) {
    const parts = String(book.s3_key)
      .replace(/^library\/?/, "")
      .split("/")
      .filter(Boolean);
    const pdfsIndex = parts.lastIndexOf("pdfs");
    const rawParts = pdfsIndex >= 0 ? parts.slice(0, pdfsIndex) : parts.slice(0, -1);
    folderPath = normalizeFolderPathNames(rawParts.filter((part) => part !== "_uncategorized"));
  }

  return { ...book, folder_path: folderPath };
};

/** Resolve a folder record to its logical path segments (Postgres or S3 path id). */
const resolveFolderPathNames = (folder, allFolders) => {
  if (!folder) return [];
  if (
    folder.source === "s3" ||
    (typeof folder.id === "string" && String(folder.id).includes("/"))
  ) {
    return String(folder.id).split("/").filter(Boolean);
  }
  const path = [];
  let current = folder;
  while (current) {
    path.unshift(current.name);
    current = current.parentId ? allFolders.find((f) => f.id === current.parentId) : null;
  }
  return path;
};

const resolveFolderChainFromPath = (pathStr, folders) => {
  if (!pathStr) return [];
  const parts = pathStr.split("/").filter(Boolean);
  const chain = [];
  let currentParentId = null;

  for (const part of parts) {
    const folder = folders.find((f) => f.name === part && f.parentId === currentParentId);
    if (!folder) break;
    chain.push(folder);
    currentParentId = folder.id;
  }
  return chain;
};

function UploadLibrary() {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Folder hierarchy display state (Legacy)
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Google Drive Style UI State
  const [allFolders, setAllFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc, name_desc, date_desc
  const [folderStyle, setFolderStyle] = useState("amber3d"); // "amber3d" | "minimalFlat" | "neonGlow" | "stackedPaper" | "glassmorphism"

  // Upload wizard state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [checkingPages, setCheckingPages] = useState(false);
  const [splittingPdf, setSplittingPdf] = useState(false);

  // Step 1: File selection state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [needsSplit, setNeedsSplit] = useState(false);

  // Step 2: Page range state
  const [fromPage, setFromPage] = useState(1);
  const [toPage, setToPage] = useState(1);

  // Step 3: Folder hierarchy & topics state
  const [bookTitle, setBookTitle] = useState("");
  const [folderChain, setFolderChain] = useState([]); // Array of {id, name}
  const [levelFolders, setLevelFolders] = useState({}); // depth -> array of folders
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolderDepth, setCreatingFolderDepth] = useState(0);
  const [creatingFolderParentId, setCreatingFolderParentId] = useState(null);

  const [topics, setTopics] = useState([""]);

  // AI Auto-Detect state
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [detectedChapters, setDetectedChapters] = useState([]);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [aiMode, setAiMode] = useState(false);
  const [batchUploading, setBatchUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Configuration - page limit comes from backend /config/limits (env-driven)
  // 0 or less means "no limit loaded yet"
  const [maxPagesWithoutSplit, setMaxPagesWithoutSplit] = useState(0);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Share snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // AI Frontend URL for share links (this should point to the ai-frontend, not the backend API)
  const AI_FRONTEND_URL = process.env.REACT_APP_AI_FRONTEND_URL || "http://localhost:5173";
  const [bookToDelete, setBookToDelete] = useState(null);

  // File Preview State
  const [previewBook, setPreviewBook] = useState(null);

  // Rename Dialog State
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'folder'|'file', item }
  const [renameValue, setRenameValue] = useState("");

  // Folder Delete Confirmation State
  const [folderDeleteDialogOpen, setFolderDeleteDialogOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Move Dialog State
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // { type: 'folder'|'file', item }
  const [moveDestinationId, setMoveDestinationId] = useState(null);

  // Context Menu State
  const [contextMenu, setContextMenu] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleContextMenu = useCallback((event, type, item) => {
    event.preventDefault();
    setContextMenu((prev) => {
      const itemId = item.id || item.book_id;
      const prevId = prev && prev.item ? prev.item.id || prev.item.book_id : null;
      if (prev && prevId === itemId) {
        return null;
      }
      return {
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
        type,
        item,
      };
    });
  }, []);

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const steps = needsSplit
    ? ["Select File", "Choose Pages", "Organize"]
    : ["Select File", "Organize"];

  // Fetch books on mount
  const fetchBooks = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API_BASE_URL}/pdf-ingestion/list?refreshUrls=0`, config);
      const nextBooks = (response.data.books || []).map(normalizeBookForLibrary);
      setBooks(nextBooks);
      if (Array.isArray(response.data.folders) && response.data.folders.length > 0) {
        setAllFolders((prev) => {
          const existingIds = new Set(prev.map((folder) => folder.id));
          const s3Folders = response.data.folders
            .filter((folder) => folder && folder.id && !existingIds.has(folder.id))
            .map((folder) => ({
              ...folder,
              source: folder.source || "s3",
              readOnly: folder.readOnly !== undefined ? folder.readOnly : true,
            }));
          return [...prev, ...s3Folders];
        });
      }
      setError(null);
    } catch (err) {
      console.error("Failed to fetch books:", err);
      setError("Failed to load library. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBooks();
    fetchAllFolders();

    // Fetch configuration
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/config/limits`);
        if (response.data && response.data.pdfPageLimit) {
          setMaxPagesWithoutSplit(response.data.pdfPageLimit);
        }
      } catch (err) {
        console.error("Failed to fetch config:", err);
      }
    };
    fetchConfig();
  }, [fetchBooks]);

  const fetchFoldersForDepth = useCallback(async (depth, parentId = null) => {
    try {
      const url = parentId
        ? `${API_BASE_URL}/folders?parentId=${parentId}`
        : `${API_BASE_URL}/folders`;
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(url, config);
      if (response.data && response.data.success) {
        setLevelFolders((prev) => ({ ...prev, [depth]: response.data.data }));
      }
    } catch (err) {
      console.error("Failed to fetch folders:", err);
    }
  }, []);

  const fetchAllFolders = useCallback(async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.get(`${API_BASE_URL}/folders?parentId=all`, config);
      if (response.data && response.data.success) {
        setAllFolders((prev) => {
          const nextById = new Map(prev.map((folder) => [folder.id, folder]));
          response.data.data.forEach((folder) => {
            nextById.set(folder.id, folder);
          });
          return Array.from(nextById.values());
        });
      }
    } catch (err) {
      console.error("Failed to fetch all folders:", err);
    }
  }, []);

  useEffect(() => {
    fetchFoldersForDepth(0, null);
  }, [fetchFoldersForDepth]);

  const handleFolderSelect = (depth, selectedFolderId) => {
    if (selectedFolderId === "CREATE_NEW") {
      const parentId = depth > 0 ? folderChain[depth - 1].id : null;
      setCreatingFolderDepth(depth);
      setCreatingFolderParentId(parentId);
      setNewFolderName("");
      setNewFolderDialogOpen(true);
      return;
    }

    const folder = levelFolders[depth].find((f) => f.id === selectedFolderId);
    if (!folder) return;

    // Truncate chain to current depth and add selected
    const newChain = folderChain.slice(0, depth);
    newChain.push(folder);
    setFolderChain(newChain);

    // Fetch next level
    fetchFoldersForDepth(depth + 1, folder.id);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.post(
        `${API_BASE_URL}/folders`,
        {
          name: newFolderName.trim(),
          parentId: creatingFolderParentId,
        },
        config
      );

      if (response.data && response.data.success) {
        const newFolder = response.data.data;
        // Update the current level's folders
        setLevelFolders((prev) => ({
          ...prev,
          [creatingFolderDepth]: [...(prev[creatingFolderDepth] || []), newFolder],
        }));

        // Select it automatically
        const newChain = folderChain.slice(0, creatingFolderDepth);
        newChain.push(newFolder);
        setFolderChain(newChain);

        // Fetch next level (which will be empty)
        fetchFoldersForDepth(creatingFolderDepth + 1, newFolder.id);
        fetchAllFolders();
        setNewFolderDialogOpen(false);
      }
    } catch (err) {
      console.error("Failed to create folder", err);
      setError("Failed to create folder");
    }
  };

  // Build folder tree structure from books
  const folderTree = useMemo(() => {
    const root = { name: "root", path: "", books: [], subfolders: {} };
    books.forEach((book) => {
      if (book.folder_path && book.folder_path.length > 0) {
        let current = root;
        const pathParts = [];
        book.folder_path.forEach((folderName, index) => {
          pathParts.push(folderName);
          const pathKey = pathParts.join("/");
          if (!current.subfolders[folderName]) {
            current.subfolders[folderName] = {
              name: folderName,
              path: pathKey,
              books: [],
              subfolders: {},
            };
          }
          current = current.subfolders[folderName];
          if (index === book.folder_path.length - 1) {
            current.books.push(book);
          }
        });
      } else {
        root.books.push(book);
      }
    });
    return root;
  }, [books]);

  const topLevelFolders = Object.values(folderTree.subfolders);
  const uncategorizedBooks = folderTree.books;

  const toggleFolder = (folderPath) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderPath)) {
        next.delete(folderPath);
      } else {
        next.add(folderPath);
      }
      return next;
    });
  };

  const countBooksInFolder = (folder) => {
    let count = folder.books.length;
    Object.values(folder.subfolders).forEach((sf) => {
      count += countBooksInFolder(sf);
    });
    return count;
  };

  // Recursive folder contents renderer - supports unlimited depth
  const renderFolderContents = (folder, depth = 0) => {
    const subfolderList = Object.values(folder.subfolders);
    // Dark theme colors matching the dashboard
    const bgColors = ["#1a2035", "#1e2642", "#232a47", "#283150"];
    return (
      <MDBox key={`folder-${folder.path}`} mb={2} ml={depth > 0 ? 2 : 0}>
        <Card
          sx={{
            bgcolor:
              isDraggingOver === folder.path
                ? "rgba(25, 118, 210, 0.1)"
                : bgColors[depth % bgColors.length],
            transition: "background-color 0.2s ease",
            border: isDraggingOver === folder.path ? "2px dashed #1976d2" : "none",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isDraggingOver !== folder.path) setIsDraggingOver(folder.path);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Only clear if leaving THIS element (not entering child) implies standard leave logic
            // But simplistic leave might flicker.
            // For now, strict leave is okay or check relatedTarget.
            // Simplifying: just set null.
            setIsDraggingOver(null);
          }}
          onDrop={(e) => handleFolderDrop(e, folder.path)}
        >
          <MDBox
            p={depth === 0 ? 2 : 1.5}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon color="info" fontSize={depth === 0 ? "medium" : "small"}>
                folder_open
              </Icon>
              <MDTypography variant={depth === 0 ? "h6" : "button"}>{folder.name}</MDTypography>
            </MDBox>
            <MDButton variant="text" size="small" onClick={() => toggleFolder(folder.path)}>
              Close
            </MDButton>
          </MDBox>
          <MDBox p={depth === 0 ? 2 : 1.5}>
            {subfolderList.length > 0 && (
              <MDBox mb={2}>
                <MDTypography variant="caption" color="text">
                  Subfolders
                </MDTypography>
                <Grid container spacing={1} mt={0.5}>
                  {subfolderList.map((sf) => (
                    <Grid item xs={6} sm={4} md={3} key={sf.path}>
                      <Card
                        sx={{
                          cursor: "pointer",
                          p: 1,
                          bgcolor:
                            isDraggingOver === sf.path ? "rgba(25, 118, 210, 0.2)" : "#2d3a5a",
                          "&:hover": { bgcolor: "#3a4a6a" },
                          border:
                            expandedFolders.has(sf.path) || isDraggingOver === sf.path
                              ? "1px solid #1976d2"
                              : "1px solid rgba(255,255,255,0.1)",
                          transition: "all 0.2s ease",
                          transform: isDraggingOver === sf.path ? "scale(1.02)" : "scale(1)",
                        }}
                        onClick={() => toggleFolder(sf.path)}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (isDraggingOver !== sf.path) setIsDraggingOver(sf.path);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingOver(null);
                        }}
                        onDrop={(e) => handleFolderDrop(e, sf.path)}
                      >
                        <MDBox display="flex" alignItems="center" gap={0.5}>
                          <Icon color="info" sx={{ fontSize: 16 }}>
                            {Object.keys(sf.subfolders).length > 0 ? "folder_special" : "folder"}
                          </Icon>
                          <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sf.name}
                          </MDTypography>
                        </MDBox>
                        <MDTypography variant="caption" color="text">
                          {countBooksInFolder(sf)} docs
                        </MDTypography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            )}
            {subfolderList
              .filter((sf) => expandedFolders.has(sf.path))
              .map((sf) => renderFolderContents(sf, depth + 1))}
            {folder.books.length > 0 && (
              <MDBox>
                <MDTypography variant="caption" color="text">
                  Documents ({folder.books.length})
                </MDTypography>
                <Grid container spacing={1} mt={0.5}>
                  {folder.books.map((book) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={book.book_id}>
                      <Card
                        sx={{
                          overflow: "hidden",
                          cursor: "pointer",
                          bgcolor: "#2d3a5a",
                          "&:hover": { boxShadow: 4, bgcolor: "#3a4a6a" },
                        }}
                      >
                        <MDBox
                          sx={{
                            height: depth === 0 ? 250 : 200,
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                          }}
                        >
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            aria-hidden="true"
                          >
                            <div
                              className={`flex h-16 w-16 items-center justify-center rounded-2xl ${
                                darkMode ? "bg-white/10" : "bg-white/60"
                              } shadow-lg`}
                            >
                              <Icon sx={{ fontSize: 32, color: darkMode ? "#f8fafc" : "#ef4444" }}>
                                picture_as_pdf
                              </Icon>
                            </div>
                          </div>
                          <Box
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              display: "flex",
                              gap: 0.5,
                            }}
                          >
                            <Tooltip title="Copy share link">
                              <IconButton
                                size="small"
                                sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(book);
                                }}
                              >
                                <Icon color="info" sx={{ fontSize: 14 }}>
                                  share
                                </Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBookToDelete(book);
                                  setDeleteDialogOpen(true);
                                }}
                              >
                                <Icon color="error" sx={{ fontSize: 14 }}>
                                  delete
                                </Icon>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </MDBox>
                        <MDBox p={1}>
                          <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {book.book_title}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {book.chunk_count} chunks
                          </MDTypography>
                          {Array.isArray(book.topics) && book.topics.length > 0 && (
                            <MDBox mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
                              {book.topics.map((topic, idx) => (
                                <Chip
                                  key={idx}
                                  label={topic}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: 10, height: 20 }}
                                />
                              ))}
                            </MDBox>
                          )}
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </MDBox>
            )}
          </MDBox>
        </Card>
      </MDBox>
    );
  };

  // Reset wizard state
  const resetWizard = () => {
    const hasPageLimit = maxPagesWithoutSplit > 0;

    setActiveStep(0);
    setSelectedFiles([]);
    setPdfPreviewUrl(null);
    setPageCount(null);
    setNeedsSplit(false);
    setFromPage(1);
    setToPage(hasPageLimit ? maxPagesWithoutSplit : 1);
    setBookTitle("");

    // Automatically pre-populate folderChain and levelFolders if currently inside a folder
    if (currentFolderId) {
      let current = allFolders.find((f) => f.id === currentFolderId);
      const path = [];
      while (current) {
        path.unshift(current);
        current = allFolders.find((f) => f.id === current.parentId);
      }
      setFolderChain(path);

      // Populate levelFolders for each level in the path
      const newLevelFolders = {};
      newLevelFolders[0] = allFolders.filter((f) => f.parentId === null);
      for (let i = 0; i < path.length; i++) {
        newLevelFolders[i + 1] = allFolders.filter((f) => f.parentId === path[i].id);
      }
      setLevelFolders(newLevelFolders);
    } else {
      setFolderChain([]);
      setLevelFolders({
        0: allFolders.filter((f) => f.parentId === null),
      });
    }

    setTopics([""]);
    setCheckingPages(false);
    setUploading(false);
    setSplittingPdf(false);
    // Reset AI state
    setAiAnalyzing(false);
    setDetectedChapters([]);
    setSelectedChapters([]);
    setAiMode(false);
    setBatchUploading(false);
    setUploadProgress({ current: 0, total: 0 });
  };

  // Handle AI TOC Analysis
  const handleAIAnalyze = async () => {
    const selectedFile = selectedFiles[0];
    if (!selectedFile) return;

    setAiAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axios.post(`${API_BASE_URL}/pdf-ingestion/analyze-toc`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 minutes for AI analysis
      });

      const { has_toc, chapters, message } = response.data;

      if (has_toc && chapters.length > 0) {
        setDetectedChapters(chapters);
        setSelectedChapters(chapters.map((_, i) => i)); // Select all by default
        setAiMode(true);
        setSuccess(`AI detected ${chapters.length} chapters!`);
      } else {
        setError(message || "No table of contents detected. Please use manual page selection.");
        setAiMode(false);
      }
    } catch (err) {
      console.error("AI analysis failed:", err);
      setError(
        err.response?.data?.message || "AI analysis failed. Please use manual page selection."
      );
      setAiMode(false);
    } finally {
      setAiAnalyzing(false);
    }
  };

  // Toggle chapter selection
  const toggleChapterSelection = (index) => {
    setSelectedChapters((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // Handle batch upload of selected chapters
  const handleBatchUpload = async () => {
    const selectedFile = selectedFiles[0];
    if (selectedChapters.length === 0) {
      setError("Please select at least one chapter to upload");
      return;
    }

    setBatchUploading(true);
    setUploadProgress({ current: 0, total: selectedChapters.length });

    const baseTitle = selectedFile.name.replace(/\.pdf$/i, "");
    let successCount = 0;

    for (let i = 0; i < selectedChapters.length; i++) {
      const chapterIndex = selectedChapters[i];
      const chapter = detectedChapters[chapterIndex];

      setUploadProgress({ current: i + 1, total: selectedChapters.length });

      try {
        // Split PDF for this chapter
        const chapterPdf = await splitPdf(selectedFile, chapter.start_page, chapter.end_page);

        const formData = new FormData();
        formData.append("file", chapterPdf);
        formData.append("book_title", chapter.title);

        // Set folder path: [Base PDF name, Chapter level if available]
        const folderPath = [
          ...folderChain.map((f) => f.name),
          baseTitle,
          ...(chapter.suggested_folder_level ? [chapter.suggested_folder_level] : []),
        ];
        formData.append("folder_path", JSON.stringify(folderPath));

        const token = localStorage.getItem("admin_token");
        await axios.post(`${API_BASE_URL}/pdf-ingestion/upload`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          timeout: 300000,
        });

        successCount++;
      } catch (err) {
        console.error(`Failed to upload chapter: ${chapter.title}`, err);
      }
    }

    setBatchUploading(false);

    if (successCount > 0) {
      setSuccess(`Successfully uploaded ${successCount} of ${selectedChapters.length} chapters!`);
      setUploadDialogOpen(false);
      resetWizard();
      fetchBooks();
    } else {
      setError("Failed to upload any chapters. Please try again.");
    }
  };

  // Process selected files (reusable for input and drop)
  const processFiles = async (files) => {
    if (files.length === 0) return;

    // Filter validation
    const validFiles = files.filter(
      (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
    );

    if (validFiles.length === 0) {
      setError("Please select valid PDF files");
      return;
    }

    if (validFiles.length < files.length) {
      setError("Some files were skipped because they are not PDFs");
    }

    setSelectedFiles(validFiles);
    setCheckingPages(true);
    setAiMode(false);
    setDetectedChapters([]);
    setSelectedChapters([]);

    // Reset previous preview
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);

    // If single file, do the standard deep check
    if (validFiles.length === 1) {
      const file = validFiles[0];
      setBookTitle(file.name.replace(/\.pdf$/i, ""));

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPdfPreviewUrl(previewUrl);

      // Check page count CLIENT-SIDE using pdf-lib
      try {
        const count = await getPageCount(file);
        setPageCount(count);

        const hasPageLimit = maxPagesWithoutSplit > 0;

        if (hasPageLimit) {
          setNeedsSplit(count > maxPagesWithoutSplit);
          setToPage(Math.min(count, maxPagesWithoutSplit));
        } else {
          // No configured limit yet: allow full range by default
          setNeedsSplit(false);
          setToPage(count);
        }
      } catch (err) {
        console.error("Failed to check page count:", err);
        setError("Failed to analyze PDF. The file might be corrupted or password-protected.");
        setPageCount(null);
      }
    } else {
      // Valid batch selection
      setBookTitle(`${validFiles.length} files selected`);
      setPageCount(null);
      setNeedsSplit(false);
    }

    setCheckingPages(false);
  };

  // Handle file selection - check page count CLIENT-SIDE
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    await processFiles(files);
  };

  // Handle Drop on Folder
  const handleFolderDrop = async (e, folderPath) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      // Set folder path for the organization step
      setFolderLevels(folderPath.split("/"));

      // Resolve and set folder chain and levels for dropdowns
      const resolvedChain = resolveFolderChainFromPath(folderPath, allFolders);
      setFolderChain(resolvedChain);

      const newLevelFolders = {};
      newLevelFolders[0] = allFolders.filter((f) => f.parentId === null);
      for (let i = 0; i < resolvedChain.length; i++) {
        newLevelFolders[i + 1] = allFolders.filter((f) => f.parentId === resolvedChain[i].id);
      }
      setLevelFolders(newLevelFolders);

      // Process files
      await processFiles(files);

      // Open dialog
      setUploadDialogOpen(true);
      setActiveStep(0); // Start at file confirmation
    }
  };

  const [isDraggingOver, setIsDraggingOver] = useState(null);

  // Handle folder level change
  const handleFolderLevelChange = (index, value) => {
    const newLevels = [...folderLevels];
    newLevels[index] = value;
    setFolderLevels(newLevels);
  };

  // Add folder level
  const addFolderLevel = () => {
    setFolderLevels([...folderLevels, ""]);
  };

  // Remove folder level
  const removeFolderLevel = (index) => {
    if (folderLevels.length > 1) {
      setFolderLevels(folderLevels.filter((_, i) => i !== index));
    }
  };

  // Topics handlers
  const handleTopicChange = (index, value) => {
    const newTopics = [...topics];
    newTopics[index] = value;
    setTopics(newTopics);
  };

  const addTopic = () => {
    setTopics([...topics, ""]);
  };

  const removeTopic = (index) => {
    if (topics.length > 1) {
      setTopics(topics.filter((_, i) => i !== index));
    }
  };

  const moveTopicUp = (index) => {
    if (index <= 0) return;
    const newTopics = [...topics];
    [newTopics[index - 1], newTopics[index]] = [newTopics[index], newTopics[index - 1]];
    setTopics(newTopics);
  };

  const moveTopicDown = (index) => {
    if (index >= topics.length - 1) return;
    const newTopics = [...topics];
    [newTopics[index], newTopics[index + 1]] = [newTopics[index + 1], newTopics[index]];
    setTopics(newTopics);
  };

  // Handle upload - with CLIENT-SIDE splitting if needed
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError("Please select at least one file");
      return;
    }

    // For single file, ensure book title is present
    if (selectedFiles.length === 1 && !bookTitle.trim()) {
      setError("Please provide a book title");
      return;
    }

    try {
      setUploading(true);
      setError(null);

      let successCount = 0;
      // BATCH UPLOAD LOOP
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Update progress/status if batch
        if (selectedFiles.length > 1) {
          // Optional: You could add a progress state for batch uploading message
          // For now, we rely on the main "Uploading..." spinner state
        }

        try {
          let fileToUpload = file;
          // Use filename as title for batch, or user input for single
          const titleToUse =
            selectedFiles.length > 1 ? file.name.replace(/\.pdf$/i, "") : bookTitle;

          // Split PDF CLIENT-SIDE if needed (ONLY for single file mode)
          if (selectedFiles.length === 1 && needsSplit) {
            setSplittingPdf(true);
            try {
              fileToUpload = await splitPdf(file, fromPage, toPage);
              console.log(
                `Split PDF: ${file.name} -> ${fileToUpload.name} (${fileToUpload.size} bytes)`
              );
            } catch (splitError) {
              console.error("Failed to split PDF:", splitError);
              throw new Error("Failed to split PDF");
            } finally {
              setSplittingPdf(false);
            }
          }

          const formData = new FormData();
          formData.append("file", fileToUpload);
          formData.append("book_title", titleToUse);

          if (folderChain.length > 0) {
            const lastFolder = folderChain[folderChain.length - 1];
            if (lastFolder) {
              formData.append("folderId", lastFolder.id);
            }
          }

          // Add topics (optional)
          const validTopics = topics.map((t) => t.trim()).filter((t) => t.length > 0);
          if (validTopics.length > 0) {
            formData.append("topics", JSON.stringify(validTopics));
          }

          const token = localStorage.getItem("admin_token");
          await axios.post(`${API_BASE_URL}/pdf-ingestion/upload`, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            timeout: 300000, // 5 minutes
          });

          successCount++;
        } catch (fileErr) {
          console.error(`Failed to upload ${file.name}:`, fileErr);
          // Continue to next file
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully uploaded ${successCount} of ${selectedFiles.length} files!`);
        setUploadDialogOpen(false);
        resetWizard();
        fetchBooks();
      } else {
        setError("Failed to upload files. Please try again.");
      }
    } catch (err) {
      console.error("Upload process failed:", err);
      setError("An unexpected error occurred during upload");
    } finally {
      setUploading(false);
      setSplittingPdf(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!bookToDelete) return;

    try {
      await axios.delete(`${API_BASE_URL}/pdf-ingestion/${bookToDelete.book_id}`);
      setSuccess(`Deleted: ${bookToDelete.book_title}`);
      setDeleteDialogOpen(false);
      setBookToDelete(null);
      fetchBooks();
    } catch (err) {
      console.error("Delete failed:", err);
      setError("Failed to delete book");
    }
  };

  // Handle share - copy link to clipboard
  const handleShare = async (book) => {
    if (!book.pdf_url) {
      setSnackbarMessage("Cannot share: PDF URL not available");
      setSnackbarOpen(true);
      return;
    }

    const encodedPdfUrl = encodeURIComponent(book.pdf_url);
    const encodedTitle = encodeURIComponent(book.book_title);
    const shareUrl =
      `${AI_FRONTEND_URL}/chat?pdfUrl=${encodedPdfUrl}` +
      `&bookId=${book.book_id}&bookTitle=${encodedTitle}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setSnackbarMessage("Share link copied to clipboard!");
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy link:", err);
      setSnackbarMessage("Failed to copy link");
      setSnackbarOpen(true);
    }
  };

  // Handle folder rename
  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    try {
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      if (renameTarget.type === "folder") {
        await axios.put(
          `${API_BASE_URL}/folders/${renameTarget.item.id}`,
          { name: renameValue.trim() },
          config
        );
        setSuccess(`Folder renamed to "${renameValue.trim()}"`);
        fetchAllFolders();
      }
      // For files, renaming is not yet supported by the backend
      setRenameDialogOpen(false);
      setRenameTarget(null);
      setRenameValue("");
    } catch (err) {
      console.error("Rename failed:", err);
      setError("Failed to rename. Please try again.");
    }
  };

  // Handle folder delete
  const handleFolderDelete = async () => {
    if (!folderToDelete) return;
    try {
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.delete(`${API_BASE_URL}/folders/${folderToDelete.id}`, config);
      setSuccess(`Folder "${folderToDelete.name}" deleted`);
      setFolderDeleteDialogOpen(false);
      setFolderToDelete(null);
      // If we were inside the deleted folder, go back to root
      if (currentFolderId === folderToDelete.id) {
        setCurrentFolderId(null);
      }
      fetchAllFolders();
      fetchBooks();
    } catch (err) {
      console.error("Folder delete failed:", err);
      setError("Failed to delete folder. Please try again.");
    }
  };

  // Handle move folder or file
  const handleMove = async () => {
    if (!moveTarget) return;
    try {
      const token = localStorage.getItem("admin_token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      if (moveTarget.type === "folder") {
        await axios.put(
          `${API_BASE_URL}/folders/${moveTarget.item.id}`,
          { parentId: moveDestinationId },
          config
        );
        setSuccess(`Folder "${moveTarget.item.name}" moved successfully`);
        fetchAllFolders();
      } else if (moveTarget.type === "file") {
        const folderPath = [];
        if (moveDestinationId) {
          let current = allFolders.find((f) => f.id === moveDestinationId);
          while (current) {
            folderPath.unshift(current.name);
            current = allFolders.find((f) => f.id === current.parentId);
          }
        }
        await axios.put(
          `${API_BASE_URL}/pdf-ingestion/${moveTarget.item.book_id}/move`,
          { folderPath },
          config
        );
        setSuccess(`File "${moveTarget.item.book_title}" moved successfully`);
        fetchBooks();
      }
      setMoveDialogOpen(false);
      setMoveTarget(null);
      setMoveDestinationId(null);
    } catch (err) {
      console.error("Move failed:", err);
      setError("Failed to move. Please try again.");
    }
  };

  // Handle next step
  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Render step content
  const renderStepContent = () => {
    if (activeStep === 0) {
      // Step 1: File Selection
      return (
        <MDBox>
          <input
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            id="pdf-file-input"
            multiple
          />
          <label htmlFor="pdf-file-input">
            <MDButton
              variant="outlined"
              color="info"
              component="span"
              fullWidth
              disabled={checkingPages}
            >
              <Icon>upload_file</Icon>&nbsp;
              {selectedFiles.length > 0
                ? `${selectedFiles.length} file${selectedFiles.length > 1 ? "s" : ""} selected`
                : "Select PDF File(s)"}
            </MDButton>
          </label>

          {checkingPages && (
            <MDBox display="flex" alignItems="center" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
              <MDTypography variant="body2" ml={2} sx={{ color: "#ffffff" }}>
                Analyzing PDF in browser...
              </MDTypography>
            </MDBox>
          )}

          {selectedFiles.length > 1 ? (
            <MDBox mt={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Batch Upload Mode:</strong> {selectedFiles.length} files selected. Page
                splitting and AI features are disabled for batch uploads.
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 200,
                  overflow: "auto",
                  p: 1,
                  bgcolor: "#202940",
                  borderColor: "rgba(255, 255, 255, 0.1)",
                }}
              >
                {selectedFiles.map((file, idx) => (
                  <MDBox
                    key={idx}
                    p={1}
                    borderBottom={
                      idx < selectedFiles.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none"
                    }
                  >
                    <MDTypography variant="button" fontWeight="medium" color="white">
                      {file.name}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      sx={{ opacity: 0.7 }}
                    >
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </MDTypography>
                  </MDBox>
                ))}
              </Paper>
            </MDBox>
          ) : (
            <>
              {pageCount && (
                <MDBox mt={3}>
                  <MDTypography variant="body2" mb={1} sx={{ color: "#ffffff" }}>
                    <strong>Pages detected:</strong> {pageCount}
                  </MDTypography>

                  {needsSplit && maxPagesWithoutSplit > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      This PDF has more than {maxPagesWithoutSplit} pages. You&apos;ll need to
                      select a page range. The splitting will happen in your browser (no server
                      load).
                    </Alert>
                  )}

                  {!needsSplit && maxPagesWithoutSplit > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      PDF is within the {maxPagesWithoutSplit}-page limit. Ready to upload!
                    </Alert>
                  )}

                  {/* PDF Preview */}
                  {pdfPreviewUrl && (
                    <MDBox
                      mt={2}
                      sx={{
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                        borderRadius: 1,
                        overflow: "hidden",
                        height: 200,
                      }}
                    >
                      <iframe
                        src={pdfPreviewUrl}
                        title="PDF Preview"
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                      />
                    </MDBox>
                  )}
                </MDBox>
              )}
            </>
          )}
        </MDBox>
      );
    }

    if (needsSplit && activeStep === 1) {
      // Step 2: Page Range (only if splitting needed)
      return (
        <MDBox>
          {maxPagesWithoutSplit > 0 && (
            <MDTypography variant="body2" mb={2} sx={{ color: "#ffffff" }}>
              Select which pages to extract (max {maxPagesWithoutSplit} pages):
            </MDTypography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="From Page"
                type="number"
                value={fromPage}
                onChange={(e) => setFromPage(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: pageCount }}
                sx={{
                  "& .MuiInputBase-input": { color: "#ffffff" },
                  "& .MuiInputLabel-root": { color: "#ced4da" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="To Page"
                type="number"
                value={toPage}
                onChange={(e) =>
                  setToPage(Math.min(pageCount, parseInt(e.target.value) || pageCount))
                }
                inputProps={{ min: fromPage, max: pageCount }}
                sx={{
                  "& .MuiInputBase-input": { color: "#ffffff" },
                  "& .MuiInputLabel-root": { color: "#ced4da" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  },
                }}
              />
            </Grid>
          </Grid>

          <MDTypography
            variant="caption"
            color="text"
            mt={2}
            display="block"
            sx={{ color: "#ffffff" }}
          >
            Extracting {Math.max(0, toPage - fromPage + 1)} pages (pages {fromPage} to {toPage})
          </MDTypography>

          {maxPagesWithoutSplit > 0 && toPage - fromPage + 1 > maxPagesWithoutSplit && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Selected range exceeds {maxPagesWithoutSplit} pages. Please reduce the range.
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            💡 PDF splitting will happen in your browser - fast and no server load!
          </Alert>

          {/* AI Auto-Detect Section */}
          <Divider sx={{ my: 3 }} />

          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <MDTypography variant="subtitle2" sx={{ color: "#ffffff" }}>
              🤖 Or use AI to auto-detect chapters
            </MDTypography>
            <MDButton
              variant="gradient"
              color="success"
              size="small"
              onClick={handleAIAnalyze}
              disabled={aiAnalyzing || batchUploading}
            >
              {aiAnalyzing ? (
                <>
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  Analyzing...
                </>
              ) : (
                <>
                  <Icon sx={{ mr: 0.5 }}>auto_awesome</Icon>
                  AI Auto-Detect
                </>
              )}
            </MDButton>
          </MDBox>

          {/* AI Detected Chapters */}
          {aiMode && detectedChapters.length > 0 && (
            <MDBox>
              <Alert severity="success" sx={{ mb: 2 }}>
                Found {detectedChapters.length} chapters! Select which ones to upload:
              </Alert>

              <Paper variant="outlined" sx={{ maxHeight: 250, overflow: "auto", p: 1 }}>
                {detectedChapters.map((chapter, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    sx={{
                      p: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      bgcolor: selectedChapters.includes(index)
                        ? "rgba(76, 175, 80, 0.1)"
                        : "transparent",
                      cursor: "pointer",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    onClick={() => toggleChapterSelection(index)}
                  >
                    <Checkbox
                      checked={selectedChapters.includes(index)}
                      onChange={() => toggleChapterSelection(index)}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    <Box flex={1}>
                      <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#ffffff" }}>
                        {chapter.title}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#ced4da" }}>
                        Pages {chapter.start_page} - {chapter.end_page} (
                        {chapter.end_page - chapter.start_page + 1} pages)
                      </MDTypography>
                    </Box>
                    {chapter.suggested_folder_level && (
                      <Chip
                        label={chapter.suggested_folder_level}
                        size="small"
                        variant="outlined"
                        color="info"
                      />
                    )}
                  </Box>
                ))}
              </Paper>

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <MDTypography variant="caption" sx={{ color: "#ced4da" }}>
                  {selectedChapters.length} of {detectedChapters.length} chapters selected
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleBatchUpload}
                  disabled={batchUploading || selectedChapters.length === 0}
                >
                  {batchUploading ? (
                    <>
                      <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                      Uploading {uploadProgress.current}/{uploadProgress.total}...
                    </>
                  ) : (
                    <>
                      <Icon sx={{ mr: 0.5 }}>cloud_upload</Icon>
                      Upload {selectedChapters.length} Chapters
                    </>
                  )}
                </MDButton>
              </MDBox>

              {batchUploading && (
                <LinearProgress
                  variant="determinate"
                  value={(uploadProgress.current / uploadProgress.total) * 100}
                  sx={{ mt: 2 }}
                />
              )}
            </MDBox>
          )}
        </MDBox>
      );
    }

    // Step 3 (or Step 2 if no split): Organize - Folder Hierarchy
    return (
      <MDBox>
        {selectedFiles.length > 1 ? (
          <MDBox mb={2}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Batch Mode:</strong> Book titles will be set to their original filenames.
            </Alert>
          </MDBox>
        ) : (
          <TextField
            fullWidth
            label="Book Title"
            value={bookTitle}
            onChange={(e) => setBookTitle(e.target.value)}
            margin="normal"
            required
            helperText="This will be the name of your document"
            sx={{
              "& .MuiInputBase-input": { color: "#ffffff" },
              "& .MuiInputLabel-root": { color: "#ced4da" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
              },
              "& .MuiFormHelperText-root": { color: "#ced4da" },
            }}
          />
        )}

        <MDTypography variant="subtitle2" mt={3} mb={1} sx={{ color: "#ffffff" }}>
          Folder Hierarchy (optional)
        </MDTypography>
        <MDTypography
          variant="caption"
          color="text"
          mb={2}
          display="block"
          sx={{ color: "#ffffff" }}
        >
          Organize your document in folders. Add levels to create nested folders.
        </MDTypography>

        {Object.keys(levelFolders).map((depthStr) => {
          const depth = parseInt(depthStr, 10);
          if (depth > folderChain.length) return null;

          const folders = levelFolders[depth] || [];
          const selectedValue = folderChain[depth] ? folderChain[depth].id : "";

          return (
            <Box key={depth} display="flex" alignItems="center" gap={1} mb={2}>
              <Chip label={`Level ${depth + 1}`} size="small" color="info" variant="outlined" />
              <FormControl
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                  },
                  "& .MuiSelect-select": { color: "#ffffff", paddingY: "8.5px" },
                  "& .MuiSvgIcon-root": { color: "#ffffff" },
                }}
              >
                <Select
                  value={selectedValue}
                  displayEmpty
                  onChange={(e) => handleFolderSelect(depth, e.target.value)}
                  renderValue={(val) => {
                    if (!val) return <span style={{ color: "#ced4da" }}>Select Folder</span>;
                    const folder = folders.find((f) => f.id === val);
                    return folder ? folder.name : "";
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Folder
                  </MenuItem>
                  {folders.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                  <Divider />
                  <MenuItem value="CREATE_NEW" sx={{ color: "#4caf50", fontWeight: "bold" }}>
                    <Icon sx={{ mr: 1, fontSize: 18 }}>add_circle</Icon> Create New Folder
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          );
        })}

        {folderChain.length > 0 && (
          <MDBox mt={2} p={2} bgcolor="#202940" borderRadius={1}>
            <MDTypography variant="caption" sx={{ color: "#ffffff" }}>
              Preview: {folderChain.map((f) => f.name).join(" → ")} →{" "}
              {selectedFiles.length > 1 ? "[Original Filenames]" : bookTitle}
            </MDTypography>
          </MDBox>
        )}

        {/* Topics in PDF */}
        <MDTypography variant="subtitle2" mt={4} mb={1} sx={{ color: "#ffffff" }}>
          Topics in PDF
        </MDTypography>
        <MDTypography
          variant="caption"
          color="text"
          mb={2}
          display="block"
          sx={{ color: "#ffffff" }}
        >
          Add one or more topics that this PDF covers. These will be stored as tags.
        </MDTypography>

        {topics.map((topic, index) => (
          <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
            <Chip label={`Topic ${index + 1}`} size="small" color="info" variant="outlined" />
            <TextField
              fullWidth
              placeholder="e.g. Algebra, Trigonometry"
              value={topic}
              onChange={(e) => handleTopicChange(index, e.target.value)}
              size="small"
              sx={{
                "& .MuiInputBase-input": { color: "#ffffff" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
                },
              }}
            />
            {topics.length > 1 && (
              <>
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveTopicUp(index)}
                      color="info"
                      disabled={index === 0}
                    >
                      <Icon>arrow_upward</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveTopicDown(index)}
                      color="info"
                      disabled={index === topics.length - 1}
                    >
                      <Icon>arrow_downward</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton size="small" onClick={() => removeTopic(index)} color="error">
                  <Icon>remove_circle</Icon>
                </IconButton>
              </>
            )}
          </Box>
        ))}

        <MDButton
          variant="text"
          color="info"
          onClick={addTopic}
          startIcon={<Icon>add_circle</Icon>}
          sx={{ mt: 1 }}
        >
          Add Topic
        </MDButton>
      </MDBox>
    );
  };

  // Check if can proceed to next step
  const canProceed = () => {
    const hasPageLimit = maxPagesWithoutSplit > 0;
    const effectiveMaxPages = hasPageLimit ? maxPagesWithoutSplit : Number.MAX_SAFE_INTEGER;
    if (activeStep === 0) {
      if (selectedFiles.length > 1) return true; // Batch always ready if files selected
      return selectedFiles.length === 1 && pageCount && !checkingPages;
    }
    if (needsSplit && activeStep === 1) {
      return (
        fromPage >= 1 &&
        toPage <= pageCount &&
        fromPage <= toPage &&
        toPage - fromPage + 1 <= effectiveMaxPages
      );
    }
    // Last step: Book title required only for single file
    if (selectedFiles.length > 1) return true;
    return bookTitle.trim() !== "";
  };

  // Check if on final step
  const isLastStep = () => activeStep === (needsSplit ? 2 : 1);

  // --- Google Drive Navigation Logic ---
  const countDocsInFolder = useCallback(
    (folder) => {
      const folderPath = resolveFolderPathNames(folder, allFolders);
      if (folderPath.length === 0) {
        return books.filter((b) => !b.folder_path || b.folder_path.length === 0).length;
      }

      return books.filter((b) => {
        const bookPath = b.folder_path || [];
        if (bookPath.length < folderPath.length) return false;
        for (let i = 0; i < folderPath.length; i++) {
          if (bookPath[i] !== folderPath[i]) return false;
        }
        return true;
      }).length;
    },
    [books, allFolders]
  );

  const navigateToFolder = useCallback(
    (folder) => {
      const pathNames = resolveFolderPathNames(folder, allFolders);
      if (pathNames.length > 0) {
        setCurrentFolderId(pathNames.join("/"));
        return;
      }
      setCurrentFolderId(folder.id);
    },
    [allFolders]
  );

  const currentBreadcrumbs = useMemo(() => {
    const crumbs = [{ id: null, name: "Library" }];
    if (!currentFolderId) return crumbs;

    const folder = allFolders.find((f) => f.id === currentFolderId);
    if (folder) {
      const path = [];
      let current = folder;
      while (current) {
        path.unshift({ id: current.id, name: current.name });
        current = current.parentId ? allFolders.find((f) => f.id === current.parentId) : null;
      }
      return [...crumbs, ...path];
    }

    // S3 path-style folder id (e.g. NDA/General_Science)
    if (String(currentFolderId).includes("/")) {
      const parts = String(currentFolderId).split("/").filter(Boolean);
      let acc = "";
      const path = parts.map((name) => {
        acc = acc ? `${acc}/${name}` : name;
        return { id: acc, name };
      });
      return [...crumbs, ...path];
    }

    if (isLibraryRootFolder(String(currentFolderId))) {
      return [...crumbs, { id: String(currentFolderId), name: String(currentFolderId) }];
    }

    return [...crumbs, { id: currentFolderId, name: String(currentFolderId) }];
  }, [currentFolderId, allFolders]);

  const currentPathNames = useMemo(() => {
    if (!currentFolderId) return [];
    if (typeof currentFolderId === "string") {
      if (currentFolderId.includes("/")) {
        return currentFolderId.split("/").filter(Boolean);
      }
      if (isLibraryRootFolder(currentFolderId)) {
        return [String(currentFolderId)];
      }
    }
    const folder = allFolders.find((f) => f.id === currentFolderId);
    return folder ? resolveFolderPathNames(folder, allFolders) : [];
  }, [currentFolderId, allFolders]);

  /** Subfolders inferred from S3 book folder_path at the current navigation depth. */
  const s3SubfoldersAtPath = useMemo(() => {
    const depth = currentPathNames.length;
    const map = new Map();

    books.forEach((book) => {
      const bookPath = book.folder_path || [];
      if (bookPath.length <= depth) return;

      for (let i = 0; i < depth; i++) {
        if (bookPath[i] !== currentPathNames[i]) return;
      }

      const name = bookPath[depth];
      if (depth === 0 && !isLibraryRootFolder(name)) return;

      const id = [...currentPathNames, name].join("/");
      if (!map.has(id)) {
        map.set(id, {
          id,
          name,
          parentId: depth === 0 ? null : currentPathNames.join("/"),
          source: "s3",
          readOnly: true,
        });
      }
    });

    return Array.from(map.values());
  }, [books, currentPathNames]);

  const currentSubfolders = useMemo(() => {
    if (searchQuery.trim()) {
      const lowerQ = searchQuery.toLowerCase();
      return allFolders.filter((f) => f.name.toLowerCase().includes(lowerQ));
    }

    const byName = new Map();

    if (!currentFolderId) {
      s3SubfoldersAtPath.forEach((folder) => {
        if (isLibraryRootFolder(folder.name)) {
          byName.set(folder.name.toUpperCase(), folder);
        }
      });
      allFolders
        .filter((f) => !f.parentId && isLibraryRootFolder(f.name))
        .forEach((folder) => {
          const key = folder.name.toUpperCase();
          if (!byName.has(key)) {
            byName.set(key, {
              id: folder.name,
              name: folder.name,
              parentId: null,
              source: folder.source || "postgres",
            });
          }
        });
    } else {
      allFolders.forEach((folder) => {
        const folderPath = resolveFolderPathNames(folder, allFolders);
        if (folderPath.length !== currentPathNames.length + 1) return;
        for (let i = 0; i < currentPathNames.length; i++) {
          if (folderPath[i] !== currentPathNames[i]) return;
        }
        byName.set(folder.name.toLowerCase(), folder);
      });
      s3SubfoldersAtPath.forEach((folder) => {
        if (!byName.has(folder.name.toLowerCase())) {
          byName.set(folder.name.toLowerCase(), folder);
        }
      });
    }

    let folders = Array.from(byName.values());
    if (sortBy === "name_asc") folders.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "name_desc") folders.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === "date_desc") {
      folders.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return folders;
  }, [allFolders, currentFolderId, currentPathNames, s3SubfoldersAtPath, searchQuery, sortBy]);

  const currentBooks = useMemo(() => {
    let result = [...books];
    if (searchQuery.trim()) {
      const lowerQ = searchQuery.toLowerCase();
      result = result.filter(
        (b) =>
          (b.book_title || "").toLowerCase().includes(lowerQ) ||
          (b.topics || []).some((t) => t.toLowerCase().includes(lowerQ))
      );
    } else {
      result = result.filter((b) => {
        const bookPath = b.folder_path || [];
        return JSON.stringify(bookPath) === JSON.stringify(currentPathNames);
      });
    }

    if (sortBy === "name_asc") {
      result.sort((a, b) => (a.book_title || "").localeCompare(b.book_title || ""));
    }
    if (sortBy === "name_desc") {
      result.sort((a, b) => (b.book_title || "").localeCompare(a.book_title || ""));
    }
    if (sortBy === "date_desc") {
      result.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return result;
  }, [books, currentPathNames, searchQuery, sortBy]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Alerts */}
        {error && (
          <MDBox mb={2}>
            <MDAlert color="error" dismissible>
              {error}
            </MDAlert>
          </MDBox>
        )}
        {success && (
          <MDBox mb={2}>
            <MDAlert color="success" dismissible>
              {success}
            </MDAlert>
          </MDBox>
        )}

        <div
          className="font-sans min-h-[calc(100vh-140px)] rounded-xl relative flex flex-col"
          onClick={handleCloseContextMenu}
        >
          {/* TOP TOOLBAR & SEARCH */}
          <div
            className={`flex flex-col md:flex-row items-center justify-between mb-8 gap-4 ${
              darkMode ? "bg-bnova-card/60 border-bnova-border" : "bg-white border-slate-200/60"
            } backdrop-blur-md p-4 rounded-xl border shadow-lg`}
          >
            <div className="relative w-full md:w-96">
              <Icon
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                  darkMode ? "text-bnova-textSecondary" : "text-gray-400"
                } text-[20px]`}
              >
                search
              </Icon>
              <input
                type="text"
                placeholder="Search documents and folders..."
                className={`pl-11 pr-4 py-2.5 w-full ${
                  darkMode
                    ? "bg-bnova-bg border-bnova-border text-bnova-textPrimary focus:ring-violet-500/40 focus:border-violet-500 placeholder-bnova-textSecondary/50"
                    : "bg-slate-50 border-slate-200 text-slate-800 focus:ring-violet-500/20 focus:border-violet-500 placeholder-gray-400"
                } border rounded-lg text-sm focus:outline-none focus:ring-2 shadow-inner transition-all`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap md:flex-nowrap items-center gap-2 sm:gap-3 w-full md:w-auto overflow-visible md:overflow-x-auto pb-2 md:pb-0 scrollbar-hide">
              <select
                className={`${
                  darkMode
                    ? "bg-bnova-bg border-bnova-border text-bnova-textSecondary focus:ring-violet-500/40"
                    : "bg-slate-50 border-slate-200 text-slate-600 focus:ring-violet-500/20"
                } border rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 cursor-pointer font-medium min-w-[150px] flex-1 md:flex-none`}
                value={folderStyle}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setFolderStyle(e.target.value)}
                title="Folder Theme"
              >
                <option value="amber3d">📂 3D Amber</option>
                <option value="minimalFlat">📁 Minimal Flat</option>
                <option value="neonGlow">✨ Neon Glow</option>
                <option value="stackedPaper">📚 Stacked Paper</option>
                <option value="glassmorphism">💎 Glassmorphism</option>
              </select>

              <select
                className={`${
                  darkMode
                    ? "bg-bnova-bg border-bnova-border text-bnova-textSecondary focus:ring-violet-500/40"
                    : "bg-slate-50 border-slate-200 text-slate-600 focus:ring-violet-500/20"
                } border rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 cursor-pointer min-w-[140px] flex-1 md:flex-none`}
                value={sortBy}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="date_desc">Newest First</option>
              </select>

              <div
                className={`flex ${
                  darkMode ? "bg-bnova-bg border-bnova-border" : "bg-slate-50 border-slate-200"
                } p-1 rounded-lg border`}
              >
                <button
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === "grid"
                      ? darkMode
                        ? "bg-bnova-card text-violet-400 shadow-sm"
                        : "bg-white text-violet-600 shadow-sm"
                      : darkMode
                      ? "text-bnova-textSecondary hover:text-bnova-textPrimary"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                  onClick={() => setViewMode("grid")}
                  title="Grid View"
                >
                  <Icon fontSize="small">grid_view</Icon>
                </button>
                <button
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === "list"
                      ? darkMode
                        ? "bg-bnova-card text-violet-400 shadow-sm"
                        : "bg-white text-violet-600 shadow-sm"
                      : darkMode
                      ? "text-bnova-textSecondary hover:text-bnova-textPrimary"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                  onClick={() => setViewMode("list")}
                  title="List View"
                >
                  <Icon fontSize="small">list</Icon>
                </button>
              </div>

              <div
                className={`w-px h-8 ${
                  darkMode ? "bg-bnova-border" : "bg-slate-200"
                } mx-1 hidden sm:block`}
              ></div>

              <button
                className={`px-4 py-2.5 ${
                  darkMode
                    ? "bg-white/5 hover:bg-white/10 border-white/10 text-bnova-textPrimary"
                    : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-700"
                } border rounded-lg text-[13px] font-medium transition-colors shadow-sm flex items-center justify-center gap-2 whitespace-nowrap flex-1 sm:flex-none`}
                onClick={() => {
                  setCreatingFolderDepth(0);
                  setCreatingFolderParentId(currentFolderId);
                  setNewFolderName("");
                  setNewFolderDialogOpen(true);
                }}
              >
                <Icon
                  fontSize="small"
                  className={darkMode ? "text-bnova-textSecondary" : "text-slate-500"}
                >
                  create_new_folder
                </Icon>{" "}
                <span className="hidden lg:inline">New Folder</span>
              </button>
              <button
                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 rounded-lg text-[13px] font-bold text-white transition-colors shadow-md flex items-center justify-center gap-2 whitespace-nowrap flex-1 sm:flex-none"
                onClick={() => {
                  resetWizard();
                  setUploadDialogOpen(true);
                }}
              >
                <Icon fontSize="small">cloud_upload</Icon> Upload PDF
              </button>
            </div>
          </div>

          {/* BREADCRUMBS */}
          <div className="flex items-center mb-8 px-2 text-[14px] font-medium">
            <div
              className={`flex items-center gap-2 ${
                darkMode ? "text-bnova-textSecondary" : "text-slate-500"
              } overflow-x-auto whitespace-nowrap scrollbar-hide`}
            >
              {currentBreadcrumbs.map((crumb, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span
                    className={`cursor-pointer ${
                      darkMode ? "hover:text-violet-400" : "hover:text-violet-600"
                    } transition-colors ${
                      idx === currentBreadcrumbs.length - 1
                        ? darkMode
                          ? "text-bnova-textPrimary font-bold"
                          : "text-slate-800 font-bold"
                        : ""
                    }`}
                    onClick={() => setCurrentFolderId(crumb.id)}
                  >
                    {crumb.name}
                  </span>
                  {idx < currentBreadcrumbs.length - 1 && (
                    <Icon
                      fontSize="small"
                      className={darkMode ? "text-gray-600" : "text-slate-400"}
                    >
                      chevron_right
                    </Icon>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CONTENT AREA */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <CircularProgress color="info" />
              </div>
            ) : (
              <div>
                {/* GRID VIEW MODE */}
                {viewMode === "grid" && (
                  <div>
                    {/* FOLDERS SECTION (GRID) */}
                    {currentSubfolders.length > 0 && (
                      <div className="mb-12">
                        <h3
                          className={`text-[12px] font-bold ${
                            darkMode ? "text-gray-500" : "text-gray-400"
                          } uppercase tracking-wider mb-6 px-2`}
                        >
                          Folders
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-4 gap-y-10">
                          {currentSubfolders.map((folder) => (
                            <div key={folder.id} className="relative group/folder">
                              <FolderItem
                                style={folderStyle}
                                folder={folder}
                                count={countDocsInFolder(folder)}
                                onClick={() => navigateToFolder(folder)}
                                onContextMenu={(e) => handleContextMenu(e, "folder", folder)}
                                darkMode={darkMode}
                              />
                              <button
                                className={`absolute top-2 right-2 p-2 rounded-lg ${
                                  darkMode
                                    ? "text-bnova-textSecondary hover:text-white hover:bg-white/15"
                                    : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                                } transition-all pointer-events-auto`}
                                style={{ zIndex: 100, transform: "translateZ(100px)" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleContextMenu(e, "folder", folder);
                                }}
                                title="More options"
                              >
                                <Icon fontSize="small">more_vert</Icon>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* FILES SECTION (GRID) */}
                    {(currentBooks.length > 0 || currentSubfolders.length === 0) && (
                      <div>
                        {currentBooks.length > 0 && (
                          <h3
                            className={`text-[12px] font-bold ${
                              darkMode ? "text-gray-500" : "text-gray-400"
                            } uppercase tracking-wider mb-6 px-2`}
                          >
                            Documents
                          </h3>
                        )}

                        {currentBooks.length === 0 ? (
                          <div
                            className={`flex flex-col items-center justify-center py-32 text-center ${
                              darkMode
                                ? "bg-[#111624]/30 border-white/5"
                                : "bg-gray-50 border-gray-200"
                            } rounded-2xl border border-dashed mx-2`}
                          >
                            <Icon
                              sx={{ fontSize: 72 }}
                              className={`${darkMode ? "text-gray-700" : "text-gray-400"} mb-4`}
                            >
                              folder_open
                            </Icon>
                            <h3
                              className={`text-[18px] font-bold ${
                                darkMode ? "text-gray-300" : "text-gray-700"
                              } mb-2`}
                            >
                              This folder is empty
                            </h3>
                            <p
                              className={`text-[14px] ${
                                darkMode ? "text-gray-500" : "text-gray-400"
                              } mb-8 max-w-sm`}
                            >
                              There are no documents or subfolders here. Upload your first PDF to
                              get started.
                            </p>
                            <button
                              className={`px-6 py-3 ${
                                darkMode
                                  ? "bg-white/5 hover:bg-white/10 border-white/10 text-gray-200"
                                  : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700"
                              } border rounded-xl text-[14px] font-bold transition-all flex items-center gap-2`}
                              onClick={() => {
                                resetWizard();
                                setUploadDialogOpen(true);
                              }}
                            >
                              <Icon>add</Icon> Upload PDF
                            </button>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-6 px-2">
                            {currentBooks.map((book) => (
                              <DarkFileCard
                                key={book.book_id}
                                book={book}
                                viewMode="grid"
                                onClick={() => setPreviewBook(book)}
                                onContextMenu={(e) => handleContextMenu(e, "file", book)}
                                darkMode={darkMode}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* LIST VIEW MODE */}
                {viewMode === "list" && (
                  <div>
                    {currentBooks.length === 0 && currentSubfolders.length === 0 ? (
                      <div
                        className={`flex flex-col items-center justify-center py-32 text-center ${
                          darkMode ? "bg-[#111624]/30 border-white/5" : "bg-gray-50 border-gray-200"
                        } rounded-2xl border border-dashed mx-2`}
                      >
                        <Icon
                          sx={{ fontSize: 72 }}
                          className={`${darkMode ? "text-gray-700" : "text-gray-400"} mb-4`}
                        >
                          folder_open
                        </Icon>
                        <h3
                          className={`text-[18px] font-bold ${
                            darkMode ? "text-gray-300" : "text-gray-700"
                          } mb-2`}
                        >
                          This folder is empty
                        </h3>
                        <p
                          className={`text-[14px] ${
                            darkMode ? "text-gray-500" : "text-gray-400"
                          } mb-8 max-w-sm`}
                        >
                          There are no documents or subfolders here. Upload your first PDF to get
                          started.
                        </p>
                        <button
                          className={`px-6 py-3 ${
                            darkMode
                              ? "bg-white/5 hover:bg-white/10 border-white/10 text-gray-200"
                              : "bg-gray-100 hover:bg-gray-200 border-gray-300 text-gray-700"
                          } border rounded-xl text-[14px] font-bold transition-all flex items-center gap-2`}
                          onClick={() => {
                            resetWizard();
                            setUploadDialogOpen(true);
                          }}
                        >
                          <Icon>add</Icon> Upload PDF
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col px-2">
                        {/* Table Header */}
                        <div
                          className={`flex items-center px-4 py-3 ${
                            darkMode
                              ? "bg-[#111624] border-[#1e293b]"
                              : "bg-gray-100 border-gray-200"
                          } border rounded-t-xl text-[12px] font-bold ${
                            darkMode ? "text-gray-500" : "text-gray-600"
                          } uppercase tracking-wider mb-2`}
                        >
                          <div className="flex-1">Name</div>
                          <div className="w-32 hidden sm:block">Date modified</div>
                          <div className="w-24 hidden md:block">Size / Chunks</div>
                        </div>

                        {/* Folders in List */}
                        {currentSubfolders.map((folder) => (
                          <div
                            key={folder.id}
                            className={`flex items-center px-4 py-3 ${
                              darkMode
                                ? "bg-bnova-surface border-bnova-border hover:bg-bnova-card/50"
                                : "bg-white border-gray-200 hover:bg-gray-50"
                            } border rounded-xl transition-colors cursor-pointer group mb-3 shadow-sm relative`}
                            onClick={() => navigateToFolder(folder)}
                            onContextMenu={(e) => handleContextMenu(e, "folder", folder)}
                          >
                            <div
                              className={`w-10 h-10 ${
                                darkMode
                                  ? "bg-bnova-bg border-bnova-border"
                                  : "bg-gray-50 border-gray-200"
                              } rounded-lg border flex items-center justify-center mr-4 text-bnova-warning`}
                            >
                              <Icon sx={{ fontSize: 24 }}>folder</Icon>
                            </div>
                            <div className="flex-1 overflow-hidden font-medium">
                              <h4
                                className={`text-[14px] font-medium ${
                                  darkMode ? "text-bnova-textPrimary" : "text-gray-800"
                                } truncate group-hover:text-bnova-primary transition-colors`}
                              >
                                {folder.name}
                              </h4>
                            </div>
                            <div
                              className={`w-32 text-[12px] ${
                                darkMode ? "text-bnova-textSecondary" : "text-gray-500"
                              } hidden sm:block`}
                            >
                              {new Date(folder.createdAt || Date.now()).toLocaleDateString()}
                            </div>
                            <div
                              className={`w-24 text-[12px] ${
                                darkMode ? "text-bnova-textSecondary" : "text-gray-500"
                              } hidden md:block`}
                            >
                              {countDocsInFolder(folder)} files
                            </div>
                            <button
                              className={`ml-2 p-1.5 rounded-lg ${
                                darkMode
                                  ? "text-bnova-textSecondary hover:text-white hover:bg-white/10"
                                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                              } transition-all`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleContextMenu(e, "folder", folder);
                              }}
                              title="More options"
                            >
                              <Icon fontSize="small">more_vert</Icon>
                            </button>
                          </div>
                        ))}

                        {/* Files in List */}
                        {currentBooks.map((book) => (
                          <DarkFileCard
                            key={book.book_id}
                            book={book}
                            viewMode="list"
                            onClick={() => setPreviewBook(book)}
                            onContextMenu={(e) => handleContextMenu(e, "file", book)}
                            darkMode={darkMode}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CONTEXT MENU */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
          }
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 180,
              borderRadius: 2,
              mt: 1,
              backgroundColor: darkMode ? "#121829" : "#ffffff",
              color: darkMode ? "#f1f5f9" : "#0f172a",
              border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
              boxShadow: darkMode
                ? "0 10px 40px -10px rgba(0,0,0,0.5)"
                : "0 10px 40px -10px rgba(0,0,0,0.08)",
            },
          }}
        >
          {contextMenu?.type === "folder" && [
            <MenuItem
              key="open"
              onClick={() => {
                setCurrentFolderId(contextMenu.item.id);
                handleCloseContextMenu();
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                folder_open
              </Icon>{" "}
              Open
            </MenuItem>,
            <Divider key="d1" sx={{ borderColor: darkMode ? "#1e293b" : "#e2e8f0" }} />,
            <MenuItem
              key="rename"
              disabled={contextMenu?.item?.readOnly}
              onClick={() => {
                const item = contextMenu.item;
                handleCloseContextMenu();
                setRenameTarget({ type: "folder", item });
                setRenameValue(item.name);
                setRenameDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                edit
              </Icon>{" "}
              Rename
            </MenuItem>,
            <MenuItem
              key="move"
              disabled={contextMenu?.item?.readOnly}
              onClick={() => {
                const item = contextMenu.item;
                handleCloseContextMenu();
                setMoveTarget({ type: "folder", item });
                setMoveDestinationId(null);
                setMoveDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                drive_file_move
              </Icon>{" "}
              Move to...
            </MenuItem>,
            <Divider key="d2" sx={{ borderColor: darkMode ? "#1e293b" : "#e2e8f0" }} />,
            <MenuItem
              key="delete"
              disabled={contextMenu?.item?.readOnly}
              onClick={() => {
                const item = contextMenu.item;
                handleCloseContextMenu();
                setFolderToDelete(item);
                setFolderDeleteDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: "#ef4444",
                "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5 }}>
                delete
              </Icon>{" "}
              Delete
            </MenuItem>,
          ]}
          {contextMenu?.type === "file" && [
            <MenuItem
              key="preview"
              onClick={() => {
                handleCloseContextMenu();
                setPreviewBook(contextMenu.item);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                visibility
              </Icon>{" "}
              Preview
            </MenuItem>,
            <MenuItem
              key="open-new-tab"
              onClick={() => {
                handleCloseContextMenu();
                if (contextMenu.item.pdf_url) {
                  window.open(contextMenu.item.pdf_url, "_blank");
                }
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                open_in_new
              </Icon>{" "}
              Open in New Tab
            </MenuItem>,
            <MenuItem
              key="share"
              onClick={() => {
                handleCloseContextMenu();
                handleShare(contextMenu.item);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                share
              </Icon>{" "}
              Share Link
            </MenuItem>,
            <Divider key="d3" sx={{ borderColor: darkMode ? "#1e293b" : "#e2e8f0" }} />,
            <MenuItem
              key="rename"
              onClick={() => {
                const item = contextMenu.item;
                handleCloseContextMenu();
                setRenameTarget({ type: "file", item });
                setRenameValue(item.book_title || "");
                setRenameDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                edit
              </Icon>{" "}
              Rename
            </MenuItem>,
            <MenuItem
              key="move"
              onClick={() => {
                const item = contextMenu.item;
                handleCloseContextMenu();
                setMoveTarget({ type: "file", item });
                setMoveDestinationId(null);
                setMoveDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: darkMode ? "#f1f5f9" : "#334155",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                drive_file_move
              </Icon>{" "}
              Move to...
            </MenuItem>,
            <Divider key="d4" sx={{ borderColor: darkMode ? "#1e293b" : "#e2e8f0" }} />,
            <MenuItem
              key="delete"
              onClick={() => {
                handleCloseContextMenu();
                setBookToDelete(contextMenu.item);
                setDeleteDialogOpen(true);
              }}
              sx={{
                fontSize: 13,
                color: "#ef4444",
                "&:hover": { backgroundColor: "rgba(239, 68, 68, 0.1)" },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5 }}>
                delete
              </Icon>{" "}
              Delete
            </MenuItem>,
          ]}
        </Menu>
      </MDBox>

      {/* File Preview Dialog */}
      <Dialog
        open={previewBook !== null}
        onClose={() => setPreviewBook(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#090d1a" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            height: "90vh",
            maxHeight: "90vh",
            width: { xs: "calc(100vw - 16px)", sm: "calc(100vw - 48px)" },
            margin: { xs: "8px", sm: "24px" },
            borderRadius: 3,
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
            py: 1.5,
            px: { xs: 1.5, sm: 3 },
            background: darkMode
              ? "linear-gradient(135deg, #121829 0%, #090d1a 100%)"
              : "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5} overflow="hidden" minWidth={0}>
            <Icon sx={{ color: "#ef4444", fontSize: 28 }}>picture_as_pdf</Icon>
            <MDTypography
              variant="h6"
              sx={{
                color: darkMode ? "#f1f5f9" : "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {previewBook?.book_title || "Document Preview"}
            </MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1}>
            {previewBook?.pdf_url && (
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  onClick={() => window.open(previewBook.pdf_url, "_blank")}
                  sx={{
                    color: darkMode ? "#94a3b8" : "#64748b",
                    "&:hover": {
                      color: darkMode ? "#e2e8f0" : "#0f172a",
                      backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Icon>open_in_new</Icon>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Share link">
              <IconButton
                size="small"
                onClick={() => previewBook && handleShare(previewBook)}
                sx={{
                  color: darkMode ? "#94a3b8" : "#64748b",
                  "&:hover": {
                    color: darkMode ? "#e2e8f0" : "#0f172a",
                    backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
                  },
                }}
              >
                <Icon>share</Icon>
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setPreviewBook(null)}
              sx={{
                color: darkMode ? "#94a3b8" : "#64748b",
                "&:hover": { color: "#ef4444", backgroundColor: "rgba(239,68,68,0.1)" },
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent
          sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >
          {previewBook?.pdf_url ? (
            <iframe
              src={`${previewBook.pdf_url}#toolbar=1&navpanes=1&scrollbar=1`}
              title={previewBook.book_title || "Document Preview"}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                flex: 1,
                backgroundColor: darkMode ? "#090d1a" : "#ffffff",
              }}
            />
          ) : (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flex={1}
              gap={2}
            >
              <Icon sx={{ fontSize: 80, color: darkMode ? "#334155" : "#cbd5e1" }}>
                insert_drive_file
              </Icon>
              <MDTypography variant="h6" sx={{ color: darkMode ? "#64748b" : "#94a3b8" }}>
                Preview not available
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: darkMode ? "#475569" : "#cbd5e1" }}>
                This document does not have a viewable URL.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Wizard Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && !checkingPages && !splittingPdf && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h5" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Upload PDF
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ pt: 2, pb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-active": { color: "#8B5CF6" },
                      "&.Mui-completed": { color: "#10B981" },
                    },
                  }}
                >
                  <span style={{ color: darkMode ? "#94a3b8" : "#475569" }}>{label}</span>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}

          {/* Show progress during upload/split */}
          {(splittingPdf || uploading) && (
            <MDBox mt={3}>
              <MDTypography variant="body2" mb={1} sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
                {splittingPdf ? "Splitting PDF in browser..." : "Uploading..."}
              </MDTypography>
              <LinearProgress />
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={() => {
              setUploadDialogOpen(false);
              resetWizard();
            }}
            disabled={uploading || splittingPdf}
            color={darkMode ? "white" : "dark"}
          >
            Cancel
          </MDButton>

          {activeStep > 0 && (
            <MDButton
              onClick={handleBack}
              disabled={uploading || splittingPdf}
              color={darkMode ? "white" : "dark"}
            >
              Back
            </MDButton>
          )}

          {!isLastStep() ? (
            <MDButton variant="gradient" color="info" onClick={handleNext} disabled={!canProceed()}>
              Next
            </MDButton>
          ) : (
            <MDButton
              variant="gradient"
              color="success"
              onClick={handleUpload}
              disabled={!canProceed() || uploading || splittingPdf}
            >
              {uploading || splittingPdf ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Upload"
              )}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Delete Book
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ color: darkMode ? "#cbd5e1" : "#475569" }}>
            Are you sure you want to delete &quot;{bookToDelete?.book_title}&quot;? This action
            cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteDialogOpen(false)} color={darkMode ? "white" : "dark"}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Create New Folder Dialog */}
      <Dialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Create New Folder
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            margin="normal"
            sx={{
              "& .MuiInputBase-input": { color: darkMode ? "#ffffff" : "#0f172a" },
              "& .MuiInputLabel-root": { color: darkMode ? "#ced4da" : "#475569" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)",
                },
                "&:hover fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.4)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setNewFolderDialogOpen(false)}
            color={darkMode ? "white" : "dark"}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="success" onClick={handleCreateFolder}>
            Create
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Rename {renameTarget?.type === "folder" ? "Folder" : "Document"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            autoFocus
            label="New Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            margin="normal"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRename();
            }}
            sx={{
              "& .MuiInputBase-input": { color: darkMode ? "#ffffff" : "#0f172a" },
              "& .MuiInputLabel-root": { color: darkMode ? "#ced4da" : "#475569" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)",
                },
                "&:hover fieldset": {
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.4)",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setRenameDialogOpen(false)} color={darkMode ? "white" : "dark"}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleRename}
            disabled={!renameValue.trim()}
          >
            Rename
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Folder Delete Confirmation Dialog */}
      <Dialog
        open={folderDeleteDialogOpen}
        onClose={() => setFolderDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Delete Folder
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ color: darkMode ? "#cbd5e1" : "#475569" }}>
            Are you sure you want to delete &quot;{folderToDelete?.name}&quot;? This will also
            delete all subfolders and notes inside it. This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setFolderDeleteDialogOpen(false)}
            color={darkMode ? "white" : "dark"}
          >
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleFolderDelete}>
            Delete Folder
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Move to Folder Dialog */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#121829" : "#ffffff",
            color: darkMode ? "#ffffff" : "#0f172a",
            border: darkMode ? "1px solid #1e293b" : "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle>
          <MDTypography variant="h6" sx={{ color: darkMode ? "#ffffff" : "#0f172a" }}>
            Move &quot;{moveTarget?.item?.name || moveTarget?.item?.book_title}&quot;
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ color: darkMode ? "#ced4da" : "#475569", mb: 2 }}>
            Select destination folder:
          </MDTypography>
          <Paper
            variant="outlined"
            sx={{
              maxHeight: 300,
              overflow: "auto",
              bgcolor: darkMode ? "#090d1a" : "#f8fafc",
              borderColor: darkMode ? "#1e293b" : "#e2e8f0",
            }}
          >
            <MenuItem
              selected={moveDestinationId === null}
              onClick={() => setMoveDestinationId(null)}
              sx={{
                fontSize: 13,
                color: moveDestinationId === null ? "#c084fc" : darkMode ? "#f1f5f9" : "#334155",
                bgcolor: moveDestinationId === null ? "rgba(192,132,252,0.1)" : "transparent",
                "&:hover": {
                  backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                home
              </Icon>
              Root (No Folder)
            </MenuItem>
            <Divider sx={{ borderColor: darkMode ? "#1e293b" : "#e2e8f0" }} />
            {allFolders
              .filter((f) => f.id !== moveTarget?.item?.id)
              .map((folder) => (
                <MenuItem
                  key={folder.id}
                  selected={moveDestinationId === folder.id}
                  onClick={() => setMoveDestinationId(folder.id)}
                  sx={{
                    fontSize: 13,
                    color:
                      moveDestinationId === folder.id
                        ? "#c084fc"
                        : darkMode
                        ? "#f1f5f9"
                        : "#334155",
                    bgcolor:
                      moveDestinationId === folder.id ? "rgba(192,132,252,0.1)" : "transparent",
                    "&:hover": {
                      backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    },
                    pl: folder.parentId ? 4 : 2,
                  }}
                >
                  <Icon fontSize="small" sx={{ mr: 1.5, color: "#94a3b8" }}>
                    folder
                  </Icon>
                  {folder.name}
                </MenuItem>
              ))}
          </Paper>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setMoveDialogOpen(false)} color={darkMode ? "white" : "dark"}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleMove}>
            Move Here
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Share Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default UploadLibrary;
